import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Typography,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAuth } from 'shared/contexts/AuthContext';
import { userApi } from 'shared/services/userApi';
import { attentionCallApi, AttentionCall } from 'shared/services/attentionCallApi';
import { getRoleDisplayName } from 'shared/utils/roleUtils';
import { UserRole } from 'shared/types';

const attachmentFileName = (key: string): string => key.split('/').pop() || key;

interface UserOption {
  _id: string;
  name: string;
  email: string;
  role: string;
}

// La persona señalada puede ser un User registrado (elegido del desplegable) o
// un nombre escrito a mano: alguien en sitio sin cuenta en la plataforma
// (contratista, personal de paso). Mismo criterio que el reporte de accidente
// — el backend guarda `user` como enlace opcional y `name` como el único
// identificador obligatorio.
type PersonEntry = UserOption | string;

const entryName = (entry: PersonEntry): string =>
  typeof entry === 'string' ? entry : entry.name;
const entryKey = (entry: PersonEntry, index: number): string =>
  typeof entry === 'string' ? `custom-${index}-${entry}` : entry._id;

const severityOptions = [
  { value: 'leve' as const, label: 'Leve' },
  { value: 'moderado' as const, label: 'Moderado' },
  { value: 'grave' as const, label: 'Grave' },
];

interface AttentionCallModalProps {
  open: boolean;
  onClose: () => void;
  companyId?: string;
  editingCall?: AttentionCall | null;
}

const toLocalInputValue = (date: Date): string => {
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
};

export const AttentionCallModal: React.FC<AttentionCallModalProps> = ({ open, onClose, companyId: propCompanyId, editingCall }) => {
  const { user } = useAuth();
  const isEditing = !!editingCall;

  const [description, setDescription] = useState('');
  const [occurredAt, setOccurredAt] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<PersonEntry[]>([]);
  // Sostiene lo tecleado en "Persona(s) involucrada(s)" para que un nombre
  // escrito y nunca confirmado con Enter no se pierda al hacer clic afuera:
  // el onBlur de abajo confirma lo que haya quedado. El ref respalda esa
  // lectura para que vea el '' recién reseteado por un Enter de la misma
  // interacción y no un valor viejo.
  const [peopleInputValue, setPeopleInputValue] = useState('');
  const peopleInputRef = useRef('');
  const [severity, setSeverity] = useState<'leve' | 'moderado' | 'grave'>('moderado');
  const [fineAmount, setFineAmount] = useState<string>('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<string[]>([]);
  const [companyUsers, setCompanyUsers] = useState<UserOption[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Set default date/time to now, or prefill from the record being edited
  useEffect(() => {
    if (open) {
      if (editingCall) {
        setOccurredAt(toLocalInputValue(new Date(editingCall.occurredAt)));
        setDescription(editingCall.description);
        setSelectedUsers(
          editingCall.targetUsers.map((t) =>
            t.user
              ? {
                  _id: typeof t.user === 'object' ? t.user._id : t.user,
                  name: t.name,
                  email: typeof t.user === 'object' ? t.user.email : '',
                  role: t.role || '',
                }
              : t.name,
          ),
        );
        setSeverity(editingCall.severity);
        setFineAmount(editingCall.fine?.amount ? String(editingCall.fine.amount) : '');
        setExistingAttachments(editingCall.attachments || []);
      } else {
        setOccurredAt(toLocalInputValue(new Date()));
        setDescription('');
        setSelectedUsers([]);
        setSeverity('moderado');
        setFineAmount('');
        setExistingAttachments([]);
      }
      setAttachments([]);
      setError(null);
      setSuccess(false);
    }
  }, [open, editingCall]);

  // Resolve company ID from prop or user context
  const resolvedCompanyId = propCompanyId || user?.company?._id || user?.companyId || undefined;

  // Load company users
  useEffect(() => {
    if (!open || !resolvedCompanyId) return;

    const loadUsers = async () => {
      setLoadingUsers(true);
      try {
        const res = await userApi.getSimple({ company: resolvedCompanyId, limit: 100 });
        const users = (res?.data || []).map((u: any) => ({
          _id: u._id,
          name: [u.firstName, u.lastName].filter(Boolean).join(' ') || 'Sin nombre',
          email: u.email,
          role: u.role || '',
        }));
        setCompanyUsers(users);
      } catch {
        // Silently fail — dropdown will just be empty
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, [open, resolvedCompanyId]);

  const totalAttachmentCount = existingAttachments.length + attachments.length;

  const handleFileAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setAttachments((prev) => [...prev, ...newFiles].slice(0, 5 - existingAttachments.length));
    }
  };

  const handleRemoveFile = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingAttachment = (key: string) => {
    setExistingAttachments((prev) => prev.filter((k) => k !== key));
  };

  const handleViewExistingAttachment = async (key: string) => {
    if (!editingCall) return;
    const index = editingCall.attachments.indexOf(key);
    if (index === -1) return;
    try {
      const { url } = await attentionCallApi.getAttachmentUrl(editingCall._id, index);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setError('No se pudo abrir el archivo adjunto');
    }
  };

  const handleSubmit = async () => {
    if (!description.trim()) {
      setError('La descripción es requerida');
      return;
    }
    if (!occurredAt) {
      setError('La fecha es requerida');
      return;
    }
    if (selectedUsers.length === 0) {
      setError('Debe seleccionar al menos una persona involucrada');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const targetUsers = selectedUsers
        .map((entry) =>
          typeof entry === 'string'
            ? { name: entry.trim() }
            : { user: entry._id, name: entry.name, role: entry.role },
        )
        .filter((t) => t.name);

      if (isEditing && editingCall) {
        await attentionCallApi.update(editingCall._id, {
          description: description.trim(),
          occurredAt: new Date(occurredAt).toISOString(),
          targetUsers,
          severity,
          fineAmount: fineAmount ? parseFloat(fineAmount) : 0,
          keepAttachments: existingAttachments,
          attachments: attachments.length > 0 ? attachments : undefined,
        });
      } else {
        await attentionCallApi.create({
          description: description.trim(),
          occurredAt: new Date(occurredAt).toISOString(),
          companyId: resolvedCompanyId,
          targetUsers,
          severity,
          fineAmount: fineAmount ? parseFloat(fineAmount) : undefined,
          attachments: attachments.length > 0 ? attachments : undefined,
        });
      }
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || `Error al ${isEditing ? 'guardar los cambios' : 'enviar el llamado de atención'}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate min date (24 hours ago)
  const minDate = () => {
    const d = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const offset = d.getTimezoneOffset();
    const local = new Date(d.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  };

  const maxDate = () => {
    const d = new Date();
    const offset = d.getTimezoneOffset();
    const local = new Date(d.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box component="span" sx={{ fontWeight: 600 }}>
          {isEditing ? 'Editar Llamado de Atención' : 'Llamado de Atención'}
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Cerrar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {success ? (
          <Alert severity="success" sx={{ my: 2 }}>
            {isEditing ? 'Llamado de atención actualizado exitosamente' : 'Llamado de atención enviado exitosamente'}
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            {/* Description */}
            <TextField
              label="Descripción del llamado de atención"
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              fullWidth
              placeholder="Describa la situación..."
            />

            {/* Date & Time */}
            <TextField
              label="Fecha y hora del suceso"
              type="datetime-local"
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
              required
              fullWidth
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { min: minDate(), max: maxDate() },
              }}
              helperText="No puede ser más de 24 horas en el pasado"
            />

            {/* Severity */}
            <FormControl fullWidth>
              <InputLabel>Severidad</InputLabel>
              <Select
                value={severity}
                label="Severidad"
                onChange={(e) => setSeverity(e.target.value as 'leve' | 'moderado' | 'grave')}
              >
                {severityOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Fine amount (optional) */}
            <TextField
              label="Multa propuesta (USD)"
              type="number"
              value={fineAmount}
              onChange={(e) => setFineAmount(e.target.value)}
              fullWidth
              placeholder="0.00"
              slotProps={{
                htmlInput: { min: 0, step: 0.01 },
              }}
              helperText="Opcional — requiere aprobación de un supervisor"
            />

            {/* Personas señaladas: un usuario registrado del desplegable, o un
                nombre escrito a mano para quien no tiene cuenta. */}
            <Autocomplete<UserOption, true, false, true>
              multiple
              freeSolo
              options={companyUsers.filter(
                (u) => !selectedUsers.some((s) => typeof s !== 'string' && s._id === u._id),
              )}
              getOptionLabel={(opt) => entryName(opt)}
              isOptionEqualToValue={(opt, val) =>
                typeof val !== 'string' && opt._id === val._id
              }
              value={selectedUsers}
              onChange={(_, val) => {
                const cleaned = val
                  .map((entry) => (typeof entry === 'string' ? entry.trim() : entry))
                  .filter((entry) => entryName(entry));
                const seen = new Set<string>();
                const deduped = cleaned.filter((entry) => {
                  const key = entryName(entry).toLowerCase();
                  if (seen.has(key)) return false;
                  seen.add(key);
                  return true;
                });
                setSelectedUsers(deduped);
              }}
              inputValue={peopleInputValue}
              onInputChange={(_, newInputValue) => {
                peopleInputRef.current = newInputValue;
                setPeopleInputValue(newInputValue);
              }}
              onBlur={() => {
                const trimmed = peopleInputRef.current.trim();
                if (trimmed) {
                  setSelectedUsers((prev) => {
                    const isDuplicate = prev.some(
                      (entry) => entryName(entry).toLowerCase() === trimmed.toLowerCase(),
                    );
                    return isDuplicate ? prev : [...prev, trimmed];
                  });
                  peopleInputRef.current = '';
                  setPeopleInputValue('');
                }
              }}
              loading={loadingUsers}
              renderOption={(props, opt) => (
                <li {...props} key={opt._id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                      {opt.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 1, minWidth: 0 }}>
                      {opt.email}
                    </Typography>
                    <Chip label={getRoleDisplayName(opt.role as UserRole)} size="small" variant="outlined" sx={{ ml: 'auto', height: 20, fontSize: '0.65rem', flexShrink: 0 }} />
                  </Box>
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((opt, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={entryKey(opt, index)}
                    label={entryName(opt)}
                    size="small"
                    color={typeof opt === 'string' ? 'default' : 'primary'}
                    variant={typeof opt === 'string' ? 'outlined' : 'filled'}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Persona(s) involucrada(s)"
                  placeholder="Buscar usuario o escribir un nombre..."
                  required
                  helperText="Requerido — busque un usuario registrado o escriba el nombre de cualquier persona y presione Enter"
                />
              )}
            />

            {/* Attachments (images + PDFs) */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Archivos adjuntos (opcional, máx. 5 — imágenes o PDF)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {existingAttachments.map((key) => (
                  <Chip
                    key={key}
                    label={attachmentFileName(key)}
                    size="small"
                    variant="outlined"
                    onClick={() => handleViewExistingAttachment(key)}
                    onDelete={() => handleRemoveExistingAttachment(key)}
                    deleteIcon={<DeleteIcon fontSize="small" />}
                  />
                ))}
                {attachments.map((file, i) => (
                  <Chip
                    key={i}
                    label={file.name.length > 20 ? file.name.slice(0, 20) + '...' : file.name}
                    size="small"
                    onDelete={() => handleRemoveFile(i)}
                    deleteIcon={<DeleteIcon fontSize="small" />}
                  />
                ))}
                {totalAttachmentCount < 5 && (
                  <Button
                    component="label"
                    size="small"
                    variant="outlined"
                    startIcon={<AttachFileIcon />}
                  >
                    Agregar
                    <input
                      type="file"
                      hidden
                      aria-label="Seleccionar archivo adjunto"
                      accept="image/*,application/pdf"
                      multiple
                      onChange={handleFileAdd}
                    />
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="warning"
            disabled={submitting || !description.trim() || selectedUsers.length === 0}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {submitting ? (isEditing ? 'Guardando...' : 'Enviando...') : (isEditing ? 'Guardar Cambios' : 'Enviar Llamado')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
